import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Input, Select, Modal, Tooltip, Tag, Empty, Popconfirm, Spin } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faEdit, faTrash, faPaperPlane, faSearch,
  faFileAlt, faTimes, faCheck,
} from '@fortawesome/free-solid-svg-icons';
import {
  fetchAllEmailTemplates,
  saveEmailTemplate,
  editEmailTemplate,
  removeEmailTemplate,
} from '../../../Slices/salesSlice';
import RichTextEditor from '../../../blueplanitUtils/RichTextEditor';

const { Search } = Input;

const CATEGORIES = ['Cold Outreach', 'Follow-Up', 'Post-Call', 'Proposal', 'Re-Engagement', 'OPS Intro', 'Other'];

const CATEGORY_COLORS = {
  'Cold Outreach': 'blue',
  'Follow-Up':     'orange',
  'Post-Call':     'purple',
  'Proposal':      'green',
  'Re-Engagement': 'red',
  'OPS Intro':     'cyan',
  'Other':         'default',
};

// ── Template variables ────────────────────────────────────────────────────────
// These tokens are replaced with live lead data when a template is applied in EmailForm.
const TEMPLATE_VARIABLES = [
  { token: '{{agencyName}}',       label: 'Agency Name',        group: 'Lead',    example: 'ABC Staffing' },
  { token: '{{city}}',             label: 'City',               group: 'Lead',    example: 'Chicago' },
  { token: '{{state}}',            label: 'State',              group: 'Lead',    example: 'IL' },
  { token: '{{contactName}}',      label: 'Contact Name',       group: 'Contact', example: 'John Smith' },
  { token: '{{firstName}}',        label: 'First Name',         group: 'Contact', example: 'John' },
  { token: '{{contactFirstName}}', label: 'Contact First Name', group: 'Contact', example: 'John' },
  { token: '{{contactTitle}}',     label: 'Contact Title',      group: 'Contact', example: 'Owner' },
  { token: '{{senderFirstName}}',  label: 'My First Name',      group: 'Sender',  example: 'Sarah' },
  { token: '{{senderLastName}}',   label: 'My Last Name',       group: 'Sender',  example: 'Johnson' },
  { token: '{{senderFullName}}',   label: 'My Full Name',       group: 'Sender',  example: 'Sarah Johnson' },
];

const VAR_GROUPS = ['Lead', 'Contact', 'Sender'];

// Wraps {{token}} occurrences in a highlighted span for HTML body preview.
const highlightTokensInHtml = (html) => {
  if (!html) return '';
  return html.replace(
    /\{\{(\w+)\}\}/g,
    '<span style="background:#ede9fe;color:#6d28d9;border-radius:3px;padding:0 3px;font-weight:600;font-size:0.88em">{{$1}}</span>',
  );
};

// Renders plain text with {{tokens}} highlighted inline as React nodes.
const HighlightedVariableText = ({ text }) => {
  if (!text) return null;
  const parts = text.split(/(\{\{\w+\}\})/g);
  return (
    <>
      {parts.map((part, i) =>
        /^\{\{\w+\}\}$/.test(part)
          ? <span key={i} style={{ background: '#ede9fe', color: '#6d28d9', borderRadius: 3, padding: '0 3px', fontSize: '0.88em', fontWeight: 600 }}>{part}</span>
          : part
      )}
    </>
  );
};

const stripHtml = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
};

const EmptyState = ({ onNew }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: 16 }}>
    <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <FontAwesomeIcon icon={faFileAlt} style={{ fontSize: 32, color: '#6366f1' }} />
    </div>
    <div style={{ fontWeight: 700, fontSize: 16, color: '#1e293b' }}>No templates yet</div>
    <div style={{ fontSize: 13, color: '#94a3b8', textAlign: 'center', maxWidth: 300 }}>
      Create reusable email templates for cold outreach, follow-ups, proposals and more.
    </div>
    <Button
      type="primary"
      icon={<FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />}
      onClick={onNew}
      style={{ borderRadius: 8, fontWeight: 600 }}
    >
      Create First Template
    </Button>
  </div>
);

const TemplateCard = ({ template, onEdit, onDelete, onPreview }) => {
  const preview = stripHtml(template.body).slice(0, 100);
  return (
    <div
      style={{
        background: '#fff', border: '1px solid #e8edf5',
        borderRadius: 10, padding: '14px 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        transition: 'box-shadow 0.2s', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
      onClick={() => onPreview(template)}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)'; }}
    >
      {/* Top row: name + category + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <FontAwesomeIcon icon={faFileAlt} style={{ color: '#fff', fontSize: 13 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {template.name}
          </div>
          {template.category && (
            <Tag color={CATEGORY_COLORS[template.category] || 'default'} style={{ marginTop: 2, fontSize: 10, borderRadius: 20 }}>
              {template.category}
            </Tag>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
          <Tooltip title="Edit">
            <Button size="small" type="text" icon={<FontAwesomeIcon icon={faEdit} style={{ fontSize: 12, color: '#6366f1' }} />} onClick={() => onEdit(template)} />
          </Tooltip>
          <Popconfirm
            title="Delete this template?"
            description="This cannot be undone."
            onConfirm={() => onDelete(template)}
            okText="Delete"
            okType="danger"
            cancelText="Cancel"
          >
            <Tooltip title="Delete">
              <Button size="small" type="text" danger icon={<FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />} />
            </Tooltip>
          </Popconfirm>
        </div>
      </div>

      {/* Subject */}
      <div style={{ fontSize: 12, color: '#475569' }}>
        <span style={{ color: '#94a3b8', marginRight: 4 }}>Subject:</span>
        <span style={{ fontWeight: 500 }}>{template.subject || <em style={{ color: '#cbd5e1' }}>No subject</em>}</span>
      </div>

      {/* Body preview */}
      <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {preview || <em style={{ color: '#cbd5e1' }}>No content</em>}
      </div>
    </div>
  );
};

const TemplateForm = ({ initial, onSave, onCancel, loading }) => {
  const bodyRef = useRef(null);
  const subjectInputRef = useRef(null);
  const [form, setForm] = useState({ name: '', subject: '', category: '', body: '' });

  // Insert a variable token at the cursor position in the currently focused field.
  // onMouseDown + e.preventDefault() on the chip keeps focus in the active field.
  const insertVariable = (token) => {
    const nativeSubject = subjectInputRef.current?.input;
    if (document.activeElement === nativeSubject) {
      const start = nativeSubject.selectionStart;
      const end   = nativeSubject.selectionEnd;
      const next  = form.subject.slice(0, start) + token + form.subject.slice(end);
      setForm(f => ({ ...f, subject: next }));
      requestAnimationFrame(() => {
        nativeSubject.focus();
        nativeSubject.setSelectionRange(start + token.length, start + token.length);
      });
      return;
    }
    // Default: insert into body editor at current cursor
    if (bodyRef.current) {
      bodyRef.current.focus();
      document.execCommand('insertText', false, token);
    }
  };

  useEffect(() => {
    if (initial) {
      setForm({ name: initial.name || '', subject: initial.subject || '', category: initial.category || '', body: initial.body || '' });
      // Populate editor after mount
      setTimeout(() => {
        if (bodyRef.current) bodyRef.current.innerHTML = initial.body || '';
      }, 50);
    } else {
      setForm({ name: '', subject: '', category: '', body: '' });
      setTimeout(() => {
        if (bodyRef.current) bodyRef.current.innerHTML = '';
      }, 50);
    }
  }, [initial]);

  const handleSubmit = () => {
    if (!form.name.trim()) { return; }
    if (!form.subject.trim()) { return; }
    const body = bodyRef.current?.innerHTML || form.body || '';
    onSave({ ...form, body });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Template Name *</label>
          <Input
            placeholder="e.g. Cold Outreach - Agency"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div style={{ width: 180 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Category</label>
          <Select
            placeholder="Select category"
            value={form.category || undefined}
            onChange={val => setForm(f => ({ ...f, category: val }))}
            allowClear
            style={{ width: '100%' }}
            options={CATEGORIES.map(c => ({ value: c, label: c }))}
          />
        </div>
      </div>

      <div>
        <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Subject Line *</label>
        <Input
          ref={subjectInputRef}
          placeholder="e.g. Quick intro from Blueplanit — Hi {{contactFirstName}}"
          value={form.subject}
          onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
        />
      </div>

      {/* ── Variable Picker ── */}
      <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '10px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#6d28d9' }}>Dynamic Variables</span>
          <span style={{ fontSize: 11, color: '#8b5cf6' }}>— click to insert at cursor (subject or body)</span>
        </div>
        {VAR_GROUPS.map(group => (
          <div key={group} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600, minWidth: 52 }}>{group}:</span>
            {TEMPLATE_VARIABLES.filter(v => v.group === group).map(v => (
              <Tooltip key={v.token} title={<span>Inserts: <code>{v.token}</code><br />e.g. {v.example}</span>}>
                <Tag
                  color="purple"
                  style={{ cursor: 'pointer', borderRadius: 20, fontSize: 11, userSelect: 'none', margin: 0 }}
                  onMouseDown={(e) => { e.preventDefault(); insertVariable(v.token); }}
                >
                  {v.label}
                </Tag>
              </Tooltip>
            ))}
          </div>
        ))}
      </div>

      <div>
        <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Email Body *</label>
        <RichTextEditor ref={bodyRef} onChange={body => setForm(f => ({ ...f, body }))} />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
        <Button onClick={onCancel} icon={<FontAwesomeIcon icon={faTimes} style={{ marginRight: 4 }} />}>Cancel</Button>
        <Button
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          disabled={!form.name.trim() || !form.subject.trim()}
          icon={<FontAwesomeIcon icon={faCheck} style={{ marginRight: 4 }} />}
          style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none' }}
        >
          {initial ? 'Save Changes' : 'Create Template'}
        </Button>
      </div>
    </div>
  );
};

const EmailTemplates = () => {
  const dispatch = useDispatch();
  const loginUser = useSelector(state => state?.loginUserInfo?.loginUserDetails);
  const templates = useSelector(state => state?.sales?.emailTemplates || []);
  const employeeId = loginUser?.id;

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // null = new, object = editing
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (employeeId) {
      setLoading(true);
      dispatch(fetchAllEmailTemplates(employeeId)).finally(() => setLoading(false));
    }
  }, [dispatch, employeeId]);

  const filtered = templates.filter(t => {
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.subject.toLowerCase().includes(search.toLowerCase());
    const matchCat = !categoryFilter || t.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const handleSave = async (form) => {
    setSaving(true);
    let result;
    if (editing?.id) {
      result = await dispatch(editEmailTemplate({ id: editing.id, ...form }));
    } else {
      result = await dispatch(saveEmailTemplate({ ...form, employeeId }));
    }
    setSaving(false);
    if (result?.success !== false) {
      setFormOpen(false);
      setEditing(null);
    }
  };

  const handleDelete = async (template) => {
    await dispatch(removeEmailTemplate(template.id, employeeId));
  };

  const handleEdit = (template) => {
    setEditing(template);
    setFormOpen(true);
  };

  const handleNew = () => {
    setEditing(null);
    setFormOpen(true);
  };

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Page Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Email Templates</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            {templates.length} template{templates.length !== 1 ? 's' : ''} — reusable email blueprints for your outreach
          </div>
        </div>
        <Button
          type="primary"
          size="large"
          onClick={handleNew}
          icon={<FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />}
          style={{ borderRadius: 8, fontWeight: 700, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', boxShadow: '0 2px 10px #6366f155' }}
        >
          New Template
        </Button>
      </div>

      {/* ── Filters ── */}
      {templates.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <Search
            placeholder="Search by name or subject..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 200, maxWidth: 340 }}
            prefix={<FontAwesomeIcon icon={faSearch} style={{ color: '#94a3b8', fontSize: 12 }} />}
            allowClear
          />
          <Select
            placeholder="All categories"
            value={categoryFilter || undefined}
            onChange={val => setCategoryFilter(val || '')}
            allowClear
            style={{ width: 180 }}
            options={CATEGORIES.map(c => ({ value: c, label: c }))}
          />
        </div>
      )}

      {/* ── Content ── */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
          <Spin size="large" />
        </div>
      ) : templates.length === 0 ? (
        <EmptyState onNew={handleNew} />
      ) : filtered.length === 0 ? (
        <Empty description="No templates match your search" style={{ paddingTop: 40 }} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
          {filtered.map(t => (
            <TemplateCard
              key={t.id}
              template={t}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPreview={setPreviewTemplate}
            />
          ))}
        </div>
      )}

      {/* ── Create / Edit Modal ── */}
      <Modal
        open={formOpen}
        onCancel={() => { setFormOpen(false); setEditing(null); }}
        footer={null}
        width={720}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FontAwesomeIcon icon={editing ? faEdit : faPlus} style={{ color: '#fff', fontSize: 13 }} />
            </div>
            <span style={{ fontWeight: 700 }}>{editing ? 'Edit Template' : 'New Email Template'}</span>
          </div>
        }
        destroyOnClose
      >
        <TemplateForm
          initial={editing}
          onSave={handleSave}
          onCancel={() => { setFormOpen(false); setEditing(null); }}
          loading={saving}
        />
      </Modal>

      {/* ── Preview Modal ── */}
      <Modal
        open={!!previewTemplate}
        onCancel={() => setPreviewTemplate(null)}
        footer={null}
        width={680}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <FontAwesomeIcon icon={faPaperPlane} style={{ color: '#6366f1', fontSize: 14 }} />
            <span style={{ fontWeight: 700 }}>{previewTemplate?.name}</span>
            {previewTemplate?.category && (
              <Tag color={CATEGORY_COLORS[previewTemplate.category] || 'default'} style={{ borderRadius: 20, fontSize: 11 }}>
                {previewTemplate.category}
              </Tag>
            )}
          </div>
        }
      >
        {previewTemplate && (
          <div>
            <div style={{ marginBottom: 10, padding: '8px 12px', background: '#f8fafc', borderRadius: 6, border: '1px solid #e2e8f0' }}>
              <span style={{ fontSize: 12, color: '#94a3b8' }}>Subject: </span>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>
                <HighlightedVariableText text={previewTemplate.subject} />
              </span>
            </div>
            <div
              style={{ fontSize: 13, lineHeight: 1.7, color: '#334155', maxHeight: '55vh', overflowY: 'auto', padding: '4px 2px' }}
              dangerouslySetInnerHTML={{ __html: highlightTokensInHtml(previewTemplate.body) }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
              <Button onClick={() => { setPreviewTemplate(null); handleEdit(previewTemplate); }} icon={<FontAwesomeIcon icon={faEdit} style={{ marginRight: 4 }} />}>
                Edit
              </Button>
              <Button onClick={() => setPreviewTemplate(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default EmailTemplates;
